import { useCallback, useEffect, useRef, useState } from "react";
import {
  useConversationControls,
  useConversationStatus,
  useConversationMode,
  useConversationInput,
  useConversationClientTool,
} from "@elevenlabs/react";
import {
  getVoiceAgentConversationToken,
  getVoiceAgentSignedUrl,
} from "../../../network/network";

// Region the WebRTC conversation token is minted for. This MUST match the region
// your backend requests the token from (ElevenLabs account region). A mismatch
// makes the WebRTC connect fail; the hook then falls back to WebSocket.
const SERVER_LOCATION = "global";

// Auto-shutoff to bound ElevenLabs credit use on idle or abandoned sessions.
// Inactivity: end after this much silence (no agent turn-taking).
// Max session: a hard cap that ends the session regardless of activity, so
// ambient noise that keeps the agent talking can't drain credits indefinitely.
const INACTIVITY_TIMEOUT_MS = 60 * 1000;
const MAX_SESSION_MS = 5 * 60 * 1000;

const isAbortError = (error) =>
  error?.name === "AbortError" || error?.code === "ERR_CANCELED";

const readData = (response) => response?.data || response?.response?.data;

// Resolve a transport. WebRTC (conversation token) is preferred for its lower
// round-trip latency; we fall back to the WebSocket signed URL when the token
// endpoint is absent or when a prior WebRTC attempt failed (preferWebRtc=false).
const negotiateTransport = async (src, table, signal, preferWebRtc) => {
  if (preferWebRtc) {
    try {
      const tokenData = readData(
        await getVoiceAgentConversationToken(src, table, { signal }),
      );
      if (tokenData?.success && tokenData?.conversation_token) {
        return {
          connectionType: "webrtc",
          conversationToken: tokenData.conversation_token,
          // Honor the region the backend minted the token for, if provided.
          serverLocation: tokenData.server_location || SERVER_LOCATION,
        };
      }
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }
    }
  }

  const signedResponse = await getVoiceAgentSignedUrl(src, table, { signal });
  const signedData = readData(signedResponse);
  if (signedData?.success && signedData?.signed_url) {
    return { connectionType: "websocket", signedUrl: signedData.signed_url };
  }

  const transportError = new Error("voice_transport_unavailable");
  transportError.response = signedResponse;
  throw transportError;
};

/**
 * Owns the entire ElevenLabs conversation lifecycle for the QR voice agent:
 * transport negotiation, a single guarded session, mute, and status mapping.
 *
 * Must be rendered inside a `ConversationProvider`.
 *
 * @param {object} options
 * @param {boolean} options.enabled - Auto-start the session once true.
 * @param {() => ({ src: string, table: string, dynamicVariables: object, language?: string })} options.getSessionConfig
 * @param {object} options.tools - Client tool handlers keyed by agent tool name.
 * @param {(error: Error) => void} [options.onError]
 * @param {(reason: 'inactivity' | 'max_duration') => void} [options.onAutoEnd] - Called when a session is auto-ended to limit credit use.
 * @param {boolean} [options.debug]
 */
export const useVoiceAgent = ({
  enabled,
  getSessionConfig,
  tools,
  onError,
  onAutoEnd,
  debug = false,
}) => {
  const { startSession, endSession } = useConversationControls();
  const { status: connectionStatus } = useConversationStatus();
  const { isSpeaking } = useConversationMode();
  const { isMuted, setMuted } = useConversationInput();

  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState(null);

  // Client tools register their handlers via the SDK's ref pattern, so each call
  // always sees the latest cart/product state and auto-unregisters on unmount.
  useConversationClientTool("addToCart", tools.addToCart);
  useConversationClientTool("removeFromCart", tools.removeFromCart);
  useConversationClientTool("getCartContents", tools.getCartContents);
  useConversationClientTool("submitOrder", tools.submitOrder);
  useConversationClientTool("confirmOrderComplete", tools.confirmOrderComplete);

  // Refs keep start()/cleanup stable and free of stale closures.
  const startingRef = useRef(false);
  const abortedRef = useRef(false);
  const abortControllerRef = useRef(null);
  const webRtcAttemptedRef = useRef(false);
  const fallbackDoneRef = useRef(false);
  const connectionStatusRef = useRef(connectionStatus);
  const getSessionConfigRef = useRef(getSessionConfig);
  const startSessionRef = useRef(startSession);
  const endSessionRef = useRef(endSession);
  const onErrorRef = useRef(onError);
  const onAutoEndRef = useRef(onAutoEnd);
  const inactivityTimerRef = useRef(null);
  const maxSessionTimerRef = useRef(null);

  connectionStatusRef.current = connectionStatus;
  getSessionConfigRef.current = getSessionConfig;
  startSessionRef.current = startSession;
  endSessionRef.current = endSession;
  onErrorRef.current = onError;
  onAutoEndRef.current = onAutoEnd;

  const start = useCallback(async ({ preferWebRtc = true } = {}) => {
    // Synchronous guard BEFORE any await — stops this hook from issuing
    // concurrent startSession calls (StrictMode double-invoke, auto-start vs.
    // manual tap). With the provider's own de-dup, exactly one session is live.
    if (startingRef.current) {
      return;
    }
    if (
      connectionStatusRef.current === "connected" ||
      connectionStatusRef.current === "connecting"
    ) {
      return;
    }

    startingRef.current = true;
    abortedRef.current = false;
    if (preferWebRtc) {
      fallbackDoneRef.current = false;
    }
    setError(null);
    setPhase("connecting");

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const config = getSessionConfigRef.current();
      const transport = await negotiateTransport(
        config.src,
        config.table,
        controller.signal,
        preferWebRtc,
      );

      // Bailed out (navigated away / stopped) while fetching the token.
      if (abortedRef.current || controller.signal.aborted) {
        startingRef.current = false;
        return;
      }

      webRtcAttemptedRef.current = transport.connectionType === "webrtc";

      startSessionRef.current({
        ...transport,
        dynamicVariables: config.dynamicVariables,
        // No language lock — the agent auto-detects the spoken language when it
        // is configured as multilingual. A starting hint is optional.
        ...(config.language
          ? { overrides: { agent: { language: config.language } } }
          : {}),
      });
    } catch (caught) {
      startingRef.current = false;
      if (isAbortError(caught) || abortedRef.current) {
        return;
      }
      if (debug) {
        console.error("Voice agent failed to start:", caught);
      }
      setPhase("error");
      setError(caught);
      onErrorRef.current?.(caught);
    }
  }, [debug]);

  const clearTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (maxSessionTimerRef.current) {
      clearTimeout(maxSessionTimerRef.current);
      maxSessionTimerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    abortedRef.current = true;
    abortControllerRef.current?.abort();
    startingRef.current = false;
    webRtcAttemptedRef.current = false;
    fallbackDoneRef.current = false;
    clearTimers();
    endSessionRef.current?.();
    setPhase("idle");
  }, [clearTimers]);

  // Auto-end the session (idle or hard cap). stop() clears the timers.
  const endByTimeout = useCallback(
    (reason) => {
      onAutoEndRef.current?.(reason);
      stop();
    },
    [stop],
  );

  // Restart the silence countdown; called on each turn-taking change.
  const bumpInactivity = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    inactivityTimerRef.current = setTimeout(() => {
      endByTimeout("inactivity");
    }, INACTIVITY_TIMEOUT_MS);
  }, [endByTimeout]);

  const toggleMute = useCallback(() => {
    setMuted(!isMuted);
  }, [isMuted, setMuted]);

  // Drive UI phase from the SDK's real connection status.
  useEffect(() => {
    if (connectionStatus === "connected") {
      startingRef.current = false;
      webRtcAttemptedRef.current = false;
      // Connected after we already aborted (unmount/stop landed mid-connect):
      // end the orphaned session immediately so no audio leaks.
      if (abortedRef.current) {
        endSessionRef.current?.();
        return;
      }
      setError(null);
      setPhase("active");
      return;
    }

    if (connectionStatus === "error") {
      startingRef.current = false;

      // A WebRTC attempt failed at connect time (e.g. token/region mismatch).
      // Retry once over WebSocket so the user still gets a working session.
      if (
        webRtcAttemptedRef.current &&
        !fallbackDoneRef.current &&
        !abortedRef.current
      ) {
        webRtcAttemptedRef.current = false;
        fallbackDoneRef.current = true;
        start({ preferWebRtc: false });
        return;
      }

      setPhase("error");
      if (!abortedRef.current) {
        onErrorRef.current?.(new Error("voice_connection_error"));
      }
      return;
    }

    if (connectionStatus === "disconnected") {
      startingRef.current = false;
      setPhase((prev) => (prev === "error" ? prev : "idle"));
    }
  }, [connectionStatus, start]);

  // Auto-start once enabled. `start` is stable, so this fires on enable flips
  // only; the synchronous guard makes re-entry safe under StrictMode.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    start();
  }, [enabled, start]);

  // Auto-end idle or abandoned sessions to bound ElevenLabs credit use.
  useEffect(() => {
    if (phase !== "active") {
      clearTimers();
      return;
    }

    // Reset the silence countdown on every turn-taking change. An active
    // back-and-forth keeps resetting it; a silent session lets it fire.
    bumpInactivity();

    // Absolute cap — set once and deliberately not reset, so ambient noise that
    // keeps the agent talking can't keep a forgotten session alive indefinitely.
    if (!maxSessionTimerRef.current) {
      maxSessionTimerRef.current = setTimeout(() => {
        endByTimeout("max_duration");
      }, MAX_SESSION_MS);
    }
  }, [phase, isSpeaking, bumpInactivity, clearTimers, endByTimeout]);

  // End the session on unmount (navigate to cart/history/change-mode) and abort
  // any in-flight start so a connection that resolves post-unmount cannot leak.
  useEffect(() => {
    return () => {
      abortedRef.current = true;
      abortControllerRef.current?.abort();
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (maxSessionTimerRef.current) {
        clearTimeout(maxSessionTimerRef.current);
      }
      endSessionRef.current?.();
    };
  }, []);

  let status = "idle";
  if (phase === "error") {
    status = "error";
  } else if (phase === "connecting") {
    status = "connecting";
  } else if (phase === "active") {
    status = isSpeaking ? "speaking" : "listening";
  }

  return {
    status,
    isActive: phase === "active",
    isInitializing: phase === "connecting",
    isMuted,
    toggleMute,
    start,
    stop,
    error,
  };
};
